import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Form } from 'react-bootstrap';
import useStudentEmploymentRules from '@/shared/hooks/useStudentEmploymentRules';
import {
    EMPTY_EMPLOYMENT_ATTESTATION,
    STUDENT_EMPLOYMENT_REQUIREMENTS_URL,
    creditHourMinimumFor,
    formatGpa,
    studentEmploymentRequirementLines,
    unansweredCount,
    unmetAnswerCount,
    weeklyHoursLimitLine,
    withAnswer,
} from '@/shared/utils/studentEmployment';

const YES_NO = [
    { label: 'Yes', value: true },
    { label: 'No', value: false },
];

const LEVELS = [
    { label: 'Undergraduate', value: 'undergraduate' },
    { label: 'Graduate', value: 'graduate' },
];

const STILL_SUBMIT = 'You can still submit; HR makes the final decision.';

const notMet = (requirement) =>
    `You do not meet the ${requirement} requirement for student employment. ${STILL_SUBMIT}`;

// One group of radio buttons: the legend is the question, and the outcome
// sentence is announced when it appears.
function Question({ id, legend, help, options, value, outcome, onAnswer }) {
    const helpId = `${id}-help`;
    return (
        <fieldset className="mb-4" aria-describedby={help ? helpId : undefined}>
            <Form.Label as="legend" className="float-none w-auto fs-6 mb-2">
                {legend}{' '}
                <span className="text-danger" aria-hidden="true">
                    *
                </span>
                <span className="visually-hidden"> (required)</span>
            </Form.Label>
            {help && (
                <p id={helpId} className="text-muted small mb-2">
                    {help}
                </p>
            )}
            {options.map((option) => (
                <Form.Check
                    key={option.label}
                    type="radio"
                    id={`${id}-${option.label.toLowerCase()}`}
                    name={id}
                    label={option.label}
                    checked={value === option.value}
                    onChange={() => onAnswer(option.value)}
                    required
                />
            ))}
            <p
                id={`${id}-outcome`}
                role="status"
                className={outcome ? 'mt-2 mb-0' : 'mb-0'}
            >
                {outcome}
            </p>
        </fieldset>
    );
}

/**
 * The student-employment requirements, and a question for each. Controlled:
 * `value` is the seven answers and `onChange(nextAnswers)` receives the new
 * ones; nothing is kept here. An answer that means "not eligible" is
 * reported like any other and never blocks. Knows nothing about the page that
 * hosts it, so a second host can mount it with its own `idPrefix`. A host
 * short on room passes `collapsible` to fold the requirements list behind a
 * "Show the requirements" button; the questions stay open.
 */
function EmploymentRequirementsConfirm({
    value = EMPTY_EMPLOYMENT_ATTESTATION,
    onChange,
    headingAs = 'h2',
    idPrefix = 'era',
    collapsible = false,
}) {
    const [requirementsOpen, setRequirementsOpen] = useState(false);
    const { rules, loading, error, reload } = useStudentEmploymentRules();
    const [announcement, setAnnouncement] = useState('');
    const shownLegends = useRef(null);
    const questionsRef = useRef([]);

    const Heading = headingAs;
    const SubHeading = `h${Math.min(6, Number(headingAs.slice(1)) + 1)}`;
    const answer = (field) => (next) =>
        onChange(withAnswer(value, field, next));

    const creditMinimum = creditHourMinimumFor(rules, value);
    const questions = rules
        ? [
              {
                  key: 'work',
                  field: 'authorizedToWork',
                  legend: 'Are you authorized to work in the United States, and able to provide employment verification documents if hired?',
                  options: YES_NO,
                  outcome: {
                      true: 'You meet the work authorization requirement.',
                      false: notMet('work authorization'),
                  },
              },
              {
                  key: 'age',
                  field: 'meetsMinimumAge',
                  legend: `Are you at least ${rules.minimumAge} years old?`,
                  options: YES_NO,
                  outcome: {
                      true: 'You meet the age requirement.',
                      false: notMet('age'),
                  },
              },
              {
                  key: 'international',
                  field: 'isInternational',
                  legend: 'Are you an international student?',
                  help: 'International students have a higher credit-hour minimum and a lower weekly hours limit for campus jobs.',
                  options: YES_NO,
                  outcome: {
                      true: `The international student requirements apply to you: at least ${rules.internationalMinimumCreditHours.undergraduate} credit hours for an undergraduate or ${rules.internationalMinimumCreditHours.graduate} for a graduate student, and no more than ${rules.internationalWeeklyHoursLimit} hours a week across all campus jobs.`,
                  },
              },
              value.isInternational === true && {
                  key: 'level',
                  field: 'studentLevel',
                  legend: 'Are you an undergraduate or a graduate student?',
                  options: LEVELS,
                  outcome: {},
              },
              creditMinimum !== null && {
                  key: 'credits',
                  field: 'meetsCreditHours',
                  legend: `Will you be registered for at least ${creditMinimum} credit hours during the term you would work?`,
                  options: YES_NO,
                  outcome: {
                      true: 'You meet the enrollment requirement.',
                  },
              },
              value.meetsCreditHours === false && {
                  key: 'graduated',
                  field: 'graduatingSemesterException',
                  legend: 'Did you graduate?',
                  options: YES_NO,
                  outcome: {
                      true: 'You meet the enrollment requirement. A graduate may work one more semester in the same department.',
                      false: notMet('enrollment'),
                  },
              },
              {
                  key: 'gpa',
                  field: 'meetsSemesterGpa',
                  legend: `Is your GPA for your most recent semester at MSU Denver at least ${formatGpa(rules.minimumSemesterGpa)}?`,
                  help: 'If you have not finished a semester at MSU Denver yet, answer Yes. HR checks this when you are hired.',
                  options: YES_NO,
                  outcome: {
                      true: 'You meet the GPA requirement.',
                      false: notMet('GPA'),
                  },
              },
          ].filter(Boolean)
        : [];
    questionsRef.current = questions;
    const questionSignature = questions
        .map((q) => `${q.key}:${q.legend}`)
        .join('\n');

    // A question that appears is announced, and so is one whose wording
    // changes (the credit-hours minimum follows the student's level); focus
    // stays where it was. The text is cleared when the questions change
    // without either: a live region speaks only when its text changes, so a
    // sentence left in place would not be spoken when the same question comes
    // back, and it would stay in the reading order after its question is gone.
    useEffect(() => {
        if (!questionsRef.current.length) return;
        const legends = new Map(
            questionsRef.current.map((q) => [q.key, q.legend])
        );
        const previous = shownLegends.current;
        shownLegends.current = legends;
        if (!previous) return;
        const added = questionsRef.current.filter((q) => !previous.has(q.key));
        const reworded = questionsRef.current.filter(
            (q) => previous.has(q.key) && previous.get(q.key) !== q.legend
        );
        if (added.length) {
            setAnnouncement(
                `Another question was added below: ${added[added.length - 1].legend}`
            );
        } else if (reworded.length) {
            setAnnouncement(
                `A question changed: ${reworded[reworded.length - 1].legend}`
            );
        } else {
            setAnnouncement('');
        }
    }, [questionSignature]);

    const unanswered = unansweredCount(value);
    const unmet = unmetAnswerCount(value);
    let statusLine = `Answer every question to continue. ${unanswered} left.`;
    if (unanswered === 0) {
        statusLine = 'You answered every question.';
        if (unmet > 0) {
            statusLine += ` ${unmet} of your answers ${unmet === 1 ? 'does' : 'do'} not meet a requirement. ${STILL_SUBMIT}`;
        }
    }

    return (
        <div>
            <Heading className="h5 mb-3">Employment eligibility</Heading>
            <p>
                Peer support positions are student jobs, so the university's
                student-employment requirements apply. The Peer Support Hub
                cannot check your enrollment or your GPA. Human Resources
                verifies them when you are hired. Answer each question for the
                term you would work.
            </p>

            <div className="visually-hidden" role="status">
                {announcement}
            </div>

            {loading && (
                <p role="status" className="text-muted">
                    Loading the employment requirements...
                </p>
            )}

            {!loading && error && (
                <Alert variant="danger">
                    <p className="mb-2">
                        Couldn't load the employment requirements. Please try
                        again.
                    </p>
                    <Button variant="outline-danger" size="sm" onClick={reload}>
                        Try again
                    </Button>
                </Alert>
            )}

            {rules && (
                <>
                    {collapsible && (
                        <Button
                            variant="outline-primary"
                            size="sm"
                            className="mb-3"
                            aria-expanded={requirementsOpen}
                            aria-controls={`${idPrefix}-requirements`}
                            onClick={() =>
                                setRequirementsOpen(!requirementsOpen)
                            }
                        >
                            {requirementsOpen
                                ? 'Hide the requirements'
                                : 'Show the requirements'}
                        </Button>
                    )}
                    <div
                        id={`${idPrefix}-requirements`}
                        hidden={collapsible && !requirementsOpen}
                    >
                        <section
                            className="mb-4"
                            aria-labelledby={`${idPrefix}-requirements-heading`}
                        >
                            <SubHeading
                                id={`${idPrefix}-requirements-heading`}
                                className="h6"
                            >
                                The university's requirements
                            </SubHeading>
                            <ul>
                                {studentEmploymentRequirementLines(rules).map(
                                    (line) => (
                                        <li key={line}>{line}</li>
                                    )
                                )}
                            </ul>
                            <p className="mb-2">
                                {weeklyHoursLimitLine(rules)}
                            </p>
                            <a
                                href={STUDENT_EMPLOYMENT_REQUIREMENTS_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Student employment requirements (MSU Denver
                                Human Resources)
                                <span className="visually-hidden">
                                    {' '}
                                    (opens in a new tab)
                                </span>
                            </a>
                        </section>
                    </div>

                    {questions.map(({ key, field, outcome, ...question }) => (
                        <Question
                            key={key}
                            id={`${idPrefix}-${key}`}
                            value={value[field]}
                            outcome={outcome[value[field]]}
                            onAnswer={answer(field)}
                            {...question}
                        />
                    ))}

                    <p role="status" className="fw-semibold">
                        {statusLine}
                    </p>
                </>
            )}
        </div>
    );
}

export default EmploymentRequirementsConfirm;
